/**
 * Transient-prop filter for Emotion `styled(Component)` wrappers (NOA-3224 / NOA-3189).
 *
 * `styled.div` filters props through `@emotion/is-prop-valid`, so `$`-prefixed style-only
 * props never reach the DOM. But `styled(SomeComponent)` (e.g. a react-router `Link`) forwards
 * *every* prop to the wrapped component, which then spreads unknown ones onto the underlying
 * element — leaking `$`-transient props (a styled-components convention Emotion does not honour)
 * onto the DOM. That surfaces as a React hydration mismatch (#418) when the server and client
 * disagree on the leaked attribute.
 *
 * Pass this as `shouldForwardProp` whenever wrapping a component that spreads props to the DOM,
 * so `$`-props stay style-only:
 *
 *   export const Wrapper = styled(Link, { shouldForwardProp: isNotTransientProp })<{
 *     $active?: boolean;
 *   }>`
 *     color: ${({ $active }) => ($active ? 'blue' : 'inherit')};
 *   `;
 */
export const isNotTransientProp = (prop: string): boolean => !prop.startsWith('$');

const MUI_SYSTEM_PROPS = ['sx', 'as', 'theme', 'ownerState'];

/**
 * The same filter for `simbaStyled`. MUI withholds its own system props by default, but passing
 * `shouldForwardProp` replaces that default outright rather than adding to it — so they have to be
 * listed again, or `sx`/`as` reach the wrapped component and land on the DOM.
 */
export const isNotTransientOrSystemProp = (prop: string): boolean =>
  isNotTransientProp(prop) && !MUI_SYSTEM_PROPS.includes(prop);
